import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight,
  Shield, FileText, BarChart3, TrendingUp, TrendingDown, Save, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v) : "-";

export const DataVerificationPage = ({ clientId, year, month }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refTotals, setRefTotals] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/data-verification?year=${year}&month=${month}`);
      setData(res.data);
      const refs = {};
      for (const q of res.data.quadratura || []) {
        if (q.reference !== null && q.reference !== undefined) refs[q.key] = q.reference;
      }
      setRefTotals(refs);
    } catch (e) {
      if (e.response?.status === 404) {
        setData(null);
      } else {
        toast.error("Errore caricamento: " + (e.response?.data?.detail || e.message));
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, year, month]);

  useEffect(() => { load(); }, [load]);

  const saveRef = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/reference-totals?year=${year}&month=${month}`, { totals: refTotals });
      toast.success("Totali di riferimento salvati");
      load();
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleCat = (key) => setExpandedCats(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading && !data) {
    return (
      <Card><CardContent className="py-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-500" />
        <p className="text-sm text-slate-500">Caricamento verifica dati...</p>
      </CardContent></Card>
    );
  }

  if (!data) {
    return (
      <Card><CardContent className="py-12 text-center">
        <Shield className="h-8 w-8 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Nessun dato disponibile per questo periodo.</p>
        <p className="text-xs text-slate-400 mt-1">Carica un bilancio per il periodo selezionato.</p>
      </CardContent></Card>
    );
  }

  const { quadratura, categories_detail, import_log, anomalies, section_alignment, has_reference, quadratura_ok, alignment_ok, months_with_data } = data;
  const unclassifiedCount = categories_detail?.find(c => c.key === 'uncategorized')?.count || 0;

  return (
    <div className="space-y-4" data-testid="data-verification-page">
      {/* Status Banner */}
      <Card className={`border-l-4 ${
        has_reference ? (quadratura_ok ? 'border-l-green-500 bg-green-50/30' : 'border-l-red-500 bg-red-50/30') : 'border-l-amber-500 bg-amber-50/30'
      }`}>
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {has_reference ? (
              quadratura_ok ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            <div>
              <p className="text-sm font-semibold">
                {has_reference ? (quadratura_ok ? 'Quadratura OK - Dati verificati' : 'Attenzione - Scostamenti trovati') : 'Inserisci i totali di riferimento dal bilancio per la controverifica'}
              </p>
              <p className="text-xs text-slate-500">
                {import_log?.total_entries} voci importate, {import_log?.classified} classificate
                {unclassifiedCount > 0 && <span className="text-red-600 font-medium"> ({unclassifiedCount} non classificate)</span>}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-verification">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
          </Button>
        </CardContent>
      </Card>

      {/* 1. QUADRATURA */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-600" /> Quadratura
          </CardTitle>
          <p className="text-xs text-slate-400">Confronta i totali calcolati con quelli del bilancio originale</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="quadratura-table">
              <thead>
                <tr className="border-b text-slate-400">
                  <th className="text-left py-2 font-medium">Voce</th>
                  <th className="text-right py-2 font-medium">Calcolato</th>
                  <th className="text-right py-2 font-medium w-36">Riferimento (bilancio)</th>
                  <th className="text-right py-2 font-medium">Delta</th>
                  <th className="text-center py-2 font-medium w-12">Esito</th>
                </tr>
              </thead>
              <tbody>
                {quadratura?.map((q, i) => {
                  const isSummary = ['margin_contribution', 'ebitda', 'ebit', 'net_result'].includes(q.key);
                  return (
                    <tr key={q.key} className={`border-b ${isSummary ? 'bg-slate-50 font-semibold' : ''}`}>
                      <td className="py-2 text-slate-700">{q.label}</td>
                      <td className="py-2 text-right font-mono">{fmtEur(q.calculated)}</td>
                      <td className="py-2 text-right">
                        {!isSummary ? (
                          <Input
                            type="text"
                            value={refTotals[q.key] !== undefined ? refTotals[q.key] : ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^\d.,-]/g, '');
                              setRefTotals(prev => ({ ...prev, [q.key]: val === '' ? undefined : parseFloat(val.replace(',', '.')) }));
                            }}
                            placeholder="Inserisci..."
                            className="h-7 text-xs text-right w-32 inline-block"
                            data-testid={`ref-input-${q.key}`}
                          />
                        ) : (
                          <span className="font-mono text-slate-500">
                            {q.reference !== null ? fmtEur(q.reference) : '-'}
                          </span>
                        )}
                      </td>
                      <td className={`py-2 text-right font-mono ${q.delta != null ? (Math.abs(q.delta) < 1 ? 'text-green-600' : 'text-red-600') : 'text-slate-300'}`}>
                        {q.delta != null ? (q.delta >= 0 ? '+' : '') + fmtEur(q.delta) : '-'}
                      </td>
                      <td className="py-2 text-center">
                        {q.ok === true && <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />}
                        {q.ok === false && <XCircle className="h-4 w-4 text-red-500 mx-auto" />}
                        {q.ok === null && <span className="text-slate-300">-</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={saveRef} disabled={saving} data-testid="save-reference-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
              Salva Riferimenti
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 2. DETTAGLIO VOCI PER CATEGORIA */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-600" /> Dettaglio Voci per Categoria
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1" data-testid="categories-detail">
          {categories_detail?.map(cat => (
            <div key={cat.key} className={`border rounded ${cat.key === 'uncategorized' && cat.count > 0 ? 'border-red-300 bg-red-50/50' : ''}`}>
              <button
                onClick={() => toggleCat(cat.key)}
                className="w-full flex items-center justify-between p-2 hover:bg-slate-50 transition-colors text-left"
                data-testid={`toggle-category-${cat.key}`}
              >
                <div className="flex items-center gap-2">
                  {expandedCats[cat.key] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  <span className="text-xs font-medium">{cat.label}</span>
                  <Badge variant="outline" className="text-[9px]">{cat.count} voci</Badge>
                </div>
                <span className="text-xs font-mono font-semibold">{fmtEur(cat.subtotal)}</span>
              </button>
              {expandedCats[cat.key] && cat.entries?.length > 0 && (
                <div className="border-t px-2 pb-2">
                  <table className="w-full text-[11px]">
                    <tbody>
                      {cat.entries.map((e, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="py-1 text-slate-400 w-24 font-mono">{e.account_code}</td>
                          <td className="py-1 text-slate-600">{e.account_name}</td>
                          <td className="py-1 text-right font-mono">{fmtEur(Math.abs(e.amount))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 3. IMPORT LOG */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-emerald-600" /> Log Import
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Row label="Voci totali trovate" value={import_log?.total_entries} />
            <Row label="Voci classificate" value={import_log?.classified} color="text-green-600" />
            <Row label="Voci non classificate" value={import_log?.unclassified} color={import_log?.unclassified > 0 ? 'text-red-600 font-bold' : undefined} />
            <div className="border-t pt-2 mt-2">
              <p className="text-[10px] text-slate-400 mb-1">Distribuzione per categoria:</p>
              {Object.entries(import_log?.categories_count || {}).filter(([,v]) => v > 0).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[10px]">
                  <span className="text-slate-500 capitalize">{k.replace('_', ' ')}</span>
                  <span className="font-medium">{v}</span>
                </div>
              ))}
            </div>
            {import_log?.unclassified_entries?.length > 0 && (
              <div className="border-t pt-2 mt-2">
                <p className="text-[10px] text-red-500 font-medium mb-1">Voci non classificate:</p>
                {import_log.unclassified_entries.map((e, i) => (
                  <div key={i} className="text-[10px] text-slate-500 flex justify-between">
                    <span>{e.account_code} - {e.account_name}</span>
                    <span className="font-mono">{fmtEur(e.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. ANOMALIE vs MESE PRECEDENTE */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Anomalie vs Mese Precedente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {anomalies?.length === 0 ? (
              <div className="text-center py-4">
                <CheckCircle2 className="h-6 w-6 text-green-400 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Nessuna variazione anomala rilevata</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="anomalies-list">
                {anomalies?.map((a, i) => (
                  <div key={i} className={`p-2 rounded border text-xs ${
                    a.severity === 'high' ? 'bg-red-50 border-red-200' :
                    a.severity === 'medium' ? 'bg-amber-50 border-amber-200' :
                    'bg-slate-50 border-slate-200'
                  }`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium">{a.account_name}</span>
                      <Badge className={`text-[9px] ${
                        a.severity === 'high' ? 'bg-red-100 text-red-700' :
                        a.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{a.severity}</Badge>
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-500">
                      <span>Attuale: <b className="text-slate-700">{fmtEur(a.current)}</b></span>
                      <span>Prec: <b>{fmtEur(a.previous)}</b></span>
                      <span className={a.change_pct > 0 ? 'text-red-600' : 'text-green-600'}>
                        {a.change_pct != null ? (
                          <>{a.change_pct > 0 ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />} {a.change_pct > 0 ? '+' : ''}{a.change_pct}%</>
                        ) : 'Nuovo'}
                      </span>
                    </div>
                    {a.note && <p className="text-[9px] text-blue-600 mt-1">{a.note}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 5. ALLINEAMENTO SEZIONI */}
      {section_alignment?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {alignment_ok ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
              Allineamento Sezioni
            </CardTitle>
            <p className="text-xs text-slate-400">Verifica che tutte le sezioni usino gli stessi dati di base</p>
          </CardHeader>
          <CardContent data-testid="section-alignment">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-slate-400">
                  <th className="text-left py-2">Sezione</th>
                  <th className="text-left py-2">Metrica</th>
                  <th className="text-right py-2">Valore Sezione</th>
                  <th className="text-right py-2">Valore CE</th>
                  <th className="text-center py-2 w-12">Esito</th>
                </tr>
              </thead>
              <tbody>
                {section_alignment.map((s, si) =>
                  s.metrics.map((m, mi) => (
                    <tr key={`${si}-${mi}`} className="border-b">
                      {mi === 0 && <td rowSpan={s.metrics.length} className="py-2 font-medium">{s.section}</td>}
                      <td className="py-2 text-slate-600">{m.name}</td>
                      <td className="py-2 text-right font-mono">{fmtEur(m.section_value)}</td>
                      <td className="py-2 text-right font-mono">{fmtEur(m.reference_value)}</td>
                      <td className="py-2 text-center">
                        {m.ok === true && <CheckCircle2 className="h-3 w-3 text-green-500 mx-auto" />}
                        {m.ok === false && <XCircle className="h-3 w-3 text-red-500 mx-auto" />}
                        {m.ok === null && <span className="text-[9px] text-slate-400">{m.note || '-'}</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Mesi disponibili */}
      {months_with_data?.length > 1 && (
        <Card>
          <CardContent className="py-3">
            <p className="text-[10px] text-slate-400 mb-2">Mesi con dati caricati per il {year}:</p>
            <div className="flex gap-1 flex-wrap">
              {months_with_data.map(m => (
                <Badge key={m.month} variant={m.month === month ? "default" : "outline"} className="text-[9px]">
                  {new Date(2026, m.month - 1).toLocaleDateString('it-IT', { month: 'short' })} - {fmtEur(m.revenue)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Row = ({ label, value, color }) => (
  <div className="flex justify-between items-center text-xs">
    <span className="text-slate-500">{label}</span>
    <span className={`font-medium ${color || 'text-slate-700'}`}>{value}</span>
  </div>
);
